// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Del op i 360°": SAM3 on one panorama (`POST /panoramas/{id}/segment`),
 * the new UI's home for the retired `PanoramaSegmentPanel`. Shown under the
 * evidence tile's 360° strip and in the Ground's immersive 360° bar.
 *
 * ruxd tiles the equirect into `n_yaw` perspective views around the equator
 * and stitches the masks back; v1 takes **text prompts only** (no geometry
 * in equirect space), so the prompts here are class names, with the label
 * library's texts as chips — none ⟹ the model's standard classes.
 *
 * "Opret ressource fra markering" files one class of the saved equirect mask
 * as a new part through `POST /panoramas/{id}/segment/resource`, which ruxd
 * does not have yet (CONTRACT-GAPS 15): a 404 says so instead of failing
 * silently. "Alle 360°" runs the same request over every panorama in turn —
 * the in-app stand-in for `rux create annotate-360`, which has no job stage.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { ApiRequestError, api } from '../../../api/client';
import type { PanoramaSegmentResult, SegmentResourceRequest, SegmentResourceResult } from '../../../api/types';
import { useCaseStore } from '../../../app/bygning/CaseDataContext';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { useJobs } from '../../../app/JobsContext';
import { useLabelQueue } from '../../../app/LabelQueueContext';
import { useMutationQueue } from '../../../app/useMutationQueue';
import { useSam3 } from '../../../app/useSam3';
import {
  FOV_DEFAULT,
  FOV_MAX,
  FOV_MIN,
  N_YAW_DEFAULT,
  N_YAW_MAX,
  N_YAW_MIN,
  batchItems,
  batchProgress,
  clampFov,
  clampNYaw,
  confidenceText,
  nextPending,
  panoRequest,
  panoResultClasses,
  runLabel,
  setBatchStatus,
  unusedLibraryTexts,
  type BatchItem,
  type PanoPrompt,
} from '../../../bygning/delop';
import { SegmentCancelled, SegmentRunError, stopsQueue } from '../../../data/sam3Provisioning';
import type { ResultClass, SegPrompt } from '../../../data/segmentView';
import { readLabelPalette } from '../../../viewport/labelColors';
import { Button } from '../../Button';
import { IconButton, XIcon } from '../../IconButton';
import { Sam3StatusChip } from '../../Sam3StatusChip';
import { TextInput } from '../../TextInput';
import { TextLink } from '../../TextLink';
import { ResourceDialog } from '../../segmentering/ResourceDialog';
import styles from './PanoSegment.module.css';

let nextId = 0;
const newId = () => `pp${++nextId}`;

/** Missing server route for a panorama mask → resource (CONTRACT-GAPS 15). */
export const PANO_RESOURCE_MISSING =
  'Serveren kan endnu ikke oprette ressourcer fra en 360°-maske. Del op fra et foto, eller kør rux create annotate-360 og opret kortlægningen fra instanserne.';

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** A resource write's error: a 404 is the missing route (gap 15), not a missing panorama. */
function resourceErrorText(e: unknown): string {
  if (e instanceof ApiRequestError && e.isNotFound) return PANO_RESOURCE_MISSING;
  return errorText(e);
}

export interface PanoSegmentProps {
  panoramaId: number;
  /** A part was filed from the mask (the host toasts / refreshes / closes). */
  onCreated?: (result: SegmentResourceResult) => void;
}

interface Outcome {
  panoramaId: number;
  result: PanoramaSegmentResult;
  sent: string[];
}

export function PanoSegment({ panoramaId, onCreated }: PanoSegmentProps) {
  const canEdit = useCanEdit();
  const sam3 = useSam3();
  const { data, actions } = useCaseStore();
  const { markCloudsChanged } = useJobs();
  const library = useLabelQueue().labels;
  const paletteSize = Math.max(1, readLabelPalette().colors.length);

  const [prompts, setPrompts] = useState<PanoPrompt[]>([]);
  const [confidence, setConfidence] = useState(0.5);
  const [nYaw, setNYaw] = useState(N_YAW_DEFAULT);
  const [fov, setFov] = useState(FOV_DEFAULT);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [dialogFor, setDialogFor] = useState<ResultClass | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [labelNames, setLabelNames] = useState<Record<string, string> | null>(null);
  const [batch, setBatch] = useState<BatchItem[] | null>(null);
  const [batchNote, setBatchNote] = useState<string | null>(null);
  const [batchActive, setBatchActive] = useState(false);
  const stopBatch = useRef(false);

  const current = useRef(panoramaId);
  current.current = panoramaId;
  useEffect(() => {
    setOutcome(null);
    setSelected(null);
    setError(null);
  }, [panoramaId]);

  const writeQueue = useMutationQueue({ onError: (e) => setDialogError(resourceErrorText(e)) });

  const { request, sent } = panoRequest(prompts, { confidence, nYaw, fovDeg: fov });
  const asSeg: SegPrompt[] = prompts.map((p) => ({ id: p.id, text: p.text, box: null, point: false }));
  const chips = unusedLibraryTexts(library, asSeg);
  const isCurrent = outcome !== null && outcome.panoramaId === panoramaId;
  const classes = isCurrent ? panoResultClasses(outcome.result, outcome.sent) : [];
  const chosen = classes.find((c) => c.index === selected) ?? null;
  const busy = running || batchActive;

  const run = async () => {
    if (busy || !canEdit) return;
    const id = panoramaId;
    const sentNow = sent;
    setRunning(true);
    setError(null);
    try {
      const result = await sam3.run(() => api.segmentPanorama(id, request));
      if (current.current !== id) return;
      setOutcome({ panoramaId: id, result, sent: sentNow });
      const n = panoResultClasses(result, sentNow);
      setSelected(n.length === 1 ? n[0].index : null);
    } catch (e) {
      if (e instanceof SegmentCancelled) return;
      if (current.current === id) setError(e instanceof SegmentRunError ? e.message : errorText(e));
    } finally {
      setRunning(false);
    }
  };

  const runAll = async () => {
    if (busy || !canEdit) return;
    setBatchNote(null);
    let list: number[];
    try {
      list = (await api.panoramas()).map((p) => p.id);
    } catch (e) {
      setBatchNote(errorText(e));
      return;
    }
    stopBatch.current = false;
    setBatchActive(true);
    let items = batchItems(list);
    setBatch(items);
    for (let item = nextPending(items); item; item = nextPending(items)) {
      if (stopBatch.current) break;
      const pid = item.panoramaId;
      items = setBatchStatus(items, pid, 'running');
      setBatch(items);
      try {
        const result = await sam3.run(() => api.segmentPanorama(pid, request), { isCancelled: () => stopBatch.current });
        items = setBatchStatus(items, pid, 'done', { labeledPixels: result.labeled_pixels });
      } catch (e) {
        if (e instanceof SegmentCancelled) {
          items = setBatchStatus(items, pid, 'pending');
          break;
        }
        const message = e instanceof SegmentRunError ? e.message : errorText(e);
        items = setBatchStatus(items, pid, 'failed', { error: message });
        // A refusal every panorama would hit (a job holding the lock, a failed model): stop.
        if (stopsQueue(e)) {
          setBatchNote(message);
          break;
        }
      }
      setBatch(items);
    }
    setBatch(items);
    setBatchActive(false);
  };

  const openDialog = () => {
    if (!chosen) return;
    setDialogFor(chosen);
    setDialogError(null);
    api.clouds().then(
      (clouds) => setLabelNames(clouds.find((c) => c.name === 'labels')?.labels ?? null),
      () => setLabelNames(null),
    );
  };

  const createResource = (req: SegmentResourceRequest) => {
    if (!outcome) return;
    const id = outcome.panoramaId;
    void writeQueue.mutate(
      async () => {
        const result = await api.segmentPanoramaResource(id, req);
        setDialogFor(null);
        markCloudsChanged(result.clouds);
        actions.notify(`${result.resource_code} oprettet fra 360° · ${result.point_count.toLocaleString('da-DK')} punkter`);
        actions.refresh();
        onCreated?.(result);
      },
      (cause) => setDialogError(resourceErrorText(cause)),
    );
  };

  const canCreate = canEdit && isCurrent && outcome.result.saved && chosen !== null && !writeQueue.busy;
  const createWhy = !isCurrent
    ? null
    : !outcome.result.saved
      ? 'Masken blev ikke gemt.'
      : classes.length === 0
        ? 'Standardklasserne navngives ikke af serveren — skriv en tekstprompt for at kunne oprette en ressource.'
        : chosen === null
          ? 'Vælg en klasse ovenfor.'
          : null;

  return (
    <div className={styles.panel}>
      <div className={styles.row}>
        <span className={styles.title}>Del op i 360°</span>
        {sam3.available && <Sam3StatusChip view={sam3.view} compact={sam3.view.phase === 'ready' || sam3.view.phase === 'unknown'} />}
      </div>
      <p className={styles.hint}>
        Skriv de klasser, modellen skal finde rundt i hele optagelsen (fx dør, vindue). Uden prompts bruges
        standardklasserne. Punkter og bokse virker kun på fotos.
      </p>

      {prompts.length > 0 && (
        <ol className={styles.prompts}>
          {prompts.map((p) => {
            const slot = sent.indexOf(p.text.trim());
            return (
              <li key={p.id} className={styles.prompt}>
                <span
                  className={styles.swatch}
                  data-slot={slot < 0 ? 'none' : undefined}
                  style={slot < 0 ? undefined : ({ '--c': `var(--label-${slot % paletteSize})` } as CSSProperties)}
                >
                  {slot < 0 ? '·' : slot + 1}
                </span>
                <TextInput
                  variant="outline"
                  size="sm"
                  className={styles.input}
                  value={p.text}
                  placeholder="klasse, fx dør"
                  aria-label="Klassenavn"
                  disabled={busy}
                  onChange={(e) => setPrompts((cur) => cur.map((q) => (q.id === p.id ? { ...q, text: e.target.value } : q)))}
                />
                <IconButton
                  variant="ghost"
                  shape="circle"
                  label="Fjern prompt"
                  disabled={busy}
                  onClick={() => setPrompts((cur) => cur.filter((q) => q.id !== p.id))}
                >
                  <XIcon />
                </IconButton>
              </li>
            );
          })}
        </ol>
      )}
      <div className={styles.row}>
        <TextLink size="sm" disabled={busy} onClick={() => setPrompts((cur) => [...cur, { id: newId(), text: '' }])}>
          + Tekstprompt
        </TextLink>
        {chips.map((text) => (
          <button
            key={text}
            type="button"
            className={styles.chip}
            disabled={busy}
            title="Fra etiketbiblioteket"
            onClick={() => setPrompts((cur) => [...cur, { id: newId(), text }])}
          >
            + {text}
          </button>
        ))}
      </div>

      <label className={styles.slider}>
        <span>
          Konfidens <span className={styles.mono}>{confidenceText(confidence)}</span>
        </span>
        <input type="range" min={0} max={1} step={0.05} value={confidence} disabled={busy} onChange={(e) => setConfidence(Number(e.target.value))} />
      </label>

      <details className={styles.more}>
        <summary className={styles.summary}>
          Fliser <span className={styles.muted}>{request.n_yaw} om ækvator · {request.fov_deg}° synsfelt</span>
        </summary>
        <div className={styles.row}>
          <label className={styles.field}>
            Fliser
            <TextInput
              variant="outline"
              size="sm"
              className={styles.input}
              type="number"
              min={N_YAW_MIN}
              max={N_YAW_MAX}
              value={nYaw}
              disabled={busy}
              onChange={(e) => setNYaw(clampNYaw(e.target.value))}
            />
          </label>
          <label className={styles.field}>
            Synsfelt °
            <TextInput
              variant="outline"
              size="sm"
              className={styles.input}
              type="number"
              min={FOV_MIN}
              max={FOV_MAX}
              step={5}
              value={fov}
              disabled={busy}
              onChange={(e) => setFov(clampFov(e.target.value))}
            />
          </label>
        </div>
        <p className={styles.hint}>Flere fliser dækker finere; færre er hurtigere. Standard er 8 fliser à 90°.</p>
      </details>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {isCurrent && (
        <>
          <p className={styles.hint} role="status">
            {outcome.result.labeled_pixels.toLocaleString('da-DK')} px segmenteret{outcome.result.saved ? ' og gemt.' : ' (ikke gemt).'}
            {classes.length === 0 && outcome.result.labeled_pixels === 0 ? ' Modellen fandt intet — prøv en lavere konfidens.' : ''}
          </p>
          {classes.length > 0 && (
            <div className={styles.classes} role="group" aria-label="Klasser">
              {classes.map((c) => (
                <button
                  key={c.index}
                  type="button"
                  className={styles.cls}
                  aria-pressed={selected === c.index}
                  onClick={() => setSelected((s) => (s === c.index ? null : c.index))}
                >
                  <span className={styles.swatch} style={{ '--c': `var(--label-${c.index % paletteSize})` } as CSSProperties}>
                    {c.index + 1}
                  </span>
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {canEdit ? (
        <div className={styles.foot}>
          <Button onClick={() => void run()} disabled={busy} aria-busy={running}>
            {runLabel({ running, phase: sam3.view.phase, promptCount: sent.length })}
          </Button>
          <Button variant="primary" disabled={!canCreate} title={createWhy ?? undefined} aria-haspopup="dialog" onClick={openDialog}>
            Opret ressource fra markering ▾
          </Button>
        </div>
      ) : (
        <p className={styles.hint}>Kun redaktører kan dele op.</p>
      )}

      {canEdit && (
        <div className={styles.batch}>
          <div className={styles.row}>
            {batchActive ? (
              <Button onClick={() => (stopBatch.current = true)}>Stop efter denne</Button>
            ) : (
              <Button onClick={() => void runAll()} disabled={busy}>
                Kør på alle 360°-optagelser
              </Button>
            )}
            {batch && <span className={styles.muted}>{batchProgress(batch)}</span>}
          </div>
          <p className={styles.hint}>
            Samme prompts på hver optagelse efter tur og masken gemt. Til mange optagelser er{' '}
            <code className={styles.mono}>rux create annotate-360</code> på serveren hurtigere.
          </p>
          {batchNote && (
            <p className={styles.error} role="alert">
              {batchNote}
            </p>
          )}
        </div>
      )}

      {dialogFor &&
        createPortal(
          <ResourceDialog
            cls={dialogFor}
            types={[...data.types]}
            labelNames={labelNames}
            maskRevision={null}
            via="fra 360°-optagelsens position"
            busy={writeQueue.busy}
            error={dialogError}
            onCancel={() => setDialogFor(null)}
            onSubmit={createResource}
          />,
          document.body,
        )}
    </div>
  );
}
